'use server';
import { actionErrorLog } from '@/shared/infrastructure/logging/actionError';

import { and, eq } from 'drizzle-orm';
import { getDb } from '@/db';
import { savedMazes } from '@/db/schema';
import { SessionService } from '@/contexts/authentication';
import { refundMazeImage } from '../infrastructure/mazeBilling';

/**
 * Give-up path for the client poll fallback: when generation neither completed
 * nor failed inside the poll budget, release the image-maze hold so a spent token
 * NEVER lingers without a maze (the paid ⇒ persisted invariant, timeout side).
 *
 * Guard: if the maze DID persist (a late completion the poll missed), we keep the
 * charge — the user can see it in history. refund is idempotent AND a no-op on a
 * missing/terminal reservation, so calling it for a free template maze or twice
 * is harmless.
 */
export async function abandonMazeGeneration(
  mazeId: string,
): Promise<{ refunded: boolean }> {
  const ownerId = await SessionService.getFamilyId();
  if (!ownerId) return { refunded: false };

  // Never refund a maze the user can already retrieve.
  try {
    const rows = await getDb()
      .select({ id: savedMazes.id })
      .from(savedMazes)
      .where(and(eq(savedMazes.id, mazeId), eq(savedMazes.familyId, ownerId)));
    if (rows.length > 0) return { refunded: false };
  } catch (e) {
    // If we can't verify persistence, fail safe toward the user: refund below.
    actionErrorLog('abandonMazeGeneration.check', e);
  }

  try {
    await refundMazeImage(mazeId, 'Image maze generation timed out');
    return { refunded: true };
  } catch (e) {
    actionErrorLog('abandonMazeGeneration.refund', e);
    return { refunded: false };
  }
}
